import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import type { CheckoutProps } from "@/react/components/Paywalls/Checkout/CheckoutProps";
import {
  type StripeElementsMode,
  stripeElementsModes,
} from "@/react/components/Paywalls/StripeElements/types";
import type { UsePaywallCheckoutReturn } from "./usePaywallCheckout";
import { usePaywallCheckout } from "./usePaywallCheckout";

export interface CheckoutContextProps
  extends CheckoutProps,
    UsePaywallCheckoutReturn {
  // Stripe Elements state properties
  setStripeElementsMode: (value: StripeElementsMode) => void;
  stripeElementsMode: StripeElementsMode;
}

const initialState: CheckoutContextProps = {} as CheckoutContextProps;
const CheckoutContext = createContext<CheckoutContextProps>(initialState);

CheckoutContext.displayName = "CheckoutContext";

export interface CheckoutContextProviderProps extends CheckoutProps {
  children: ReactNode;
}

export const PaywallCheckoutContextProvider = (
  props: CheckoutContextProviderProps,
) => {
  const {
    children,
    isMemberAddressRequired,
    paywall,
    isTaxIdMandatory,
    isMemberWithSavedPaymentMethods,
    isPaywallsCheckoutCustomizationPhase1Enabled,
    loginUrl,
    resetPasswordUrl,
    checkoutPreviewContext,
  } = props;

  const {
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    getSelectedPrice,
    isTaxIdCollectionEnabled,
    paymentError,
    previewMutation,
    setMemberAddressAttributes,
    setPaymentError,
    setSelectedPrice,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    ...restContext
  } = usePaywallCheckout({
    isMemberAddressRequired,
    isTaxIdMandatory,
    paywall,
    isMemberWithSavedPaymentMethods,
    isPaywallsCheckoutCustomizationPhase1Enabled,
    loginUrl,
    resetPasswordUrl,
    checkoutPreviewContext,
  });

  const [stripeElementsMode, setStripeElementsMode] =
    useState<StripeElementsMode>(stripeElementsModes.payment);

  const value: CheckoutContextProps = {
    ...initialState,
    ...props,
    applyCouponCode,
    clearCouponCode,
    enableTaxIdCollection,
    getSelectedPrice,
    isTaxIdCollectionEnabled,
    paymentError,
    previewMutation,
    setMemberAddressAttributes,
    setPaymentError,
    setSelectedPrice,
    isCardInfoComplete,
    setIsCardInfoComplete,
    isCardInfoValid,
    isPaywallActive,
    isCardInfoRequired,
    isSetupIntentRequired,
    handleError,
    isProcessingPayment,
    setIsProcessingPayment,
    isMemberUsingSavedPaymentMethod,
    setIsMemberUsingSavedPaymentMethod,
    stripeElementsMode,
    setStripeElementsMode,
    checkoutPreviewContext,
    ...restContext,
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const usePaywallCheckoutContext = () => useContext(CheckoutContext);
